import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Paper,
  Stack,
  CircularProgress,
  IconButton
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { signInUser, sendPwdResetEmail } from '../services/AuthService';

import DiactiveLogo from '../assets/images/diactive-logo.png';
import HunLogo from '../assets/images/sponsor-logos/logo-hun.png';
import NavarrabiomedLogo from '../assets/images/sponsor-logos/logo-navarrabiomed.png';
import IscLogo from '../assets/images/sponsor-logos/logo-isc.png';
import FederLogo from '../assets/images/sponsor-logos/logo-feder.png';
import MinisterioLogo from '../assets/images/sponsor-logos/logo-ministerio.jpeg';
import ContactUsButton from '../components/common/ContactUsButton';

const sponsorLogoRows = [
  [
    { src: IscLogo, alt: 'Instituto de Salud Carlos III', maxHeight: 72 },
    { src: FederLogo, alt: 'Unión Europea - Fondo Europeo de Desarrollo Regional (FEDER)', maxHeight: 72 },
    { src: MinisterioLogo, alt: 'Ministerio de Ciencia e Innovación', maxHeight: 56 },
  ],
  [
    { src: HunLogo, alt: 'Hospital Universitario de Navarra', maxHeight: 56 },
    { src: NavarrabiomedLogo, alt: 'Navarrabiomed', maxHeight: 60 },
  ],
];

const fundingNavy = '#1d3461';

const SignInState = {
  ShowSignIn: 1,
  SubmitSignIn: 2,
  ShowResetPassword: 3,
  SubmitResetPassword: 4
};

const SignIn = () => {
  const { reset } = useParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signInState, setSignInState] = useState(reset ? SignInState.ShowResetPassword : SignInState.ShowSignIn);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const onEmailChange = (event) => setEmail(event.target.value);
  const onPasswordChange = (event) => setPassword(event.target.value);

  const onSubmit = async (event) => {
    event.preventDefault();
    setMessage("");

    if (signInState === SignInState.ShowSignIn) {
      setSignInState(SignInState.SubmitSignIn);
      try {
        await signInUser(email, password);
      }
      catch (error) {
        setMessage(error.message);
        setMessageType("error");
      }
      finally {
        setSignInState(SignInState.ShowSignIn);
      }
    }
    else if (signInState === SignInState.ShowResetPassword) {
      setSignInState(SignInState.SubmitResetPassword);
      const result = await sendPwdResetEmail(email);
      if (result) {
        setMessage("Se ha enviado un correo electrónico para restaurar la contraseña");
        setMessageType("success");
      }
      else {
        setMessage("El correo electrónico no es válido");
        setMessageType("error");
      }
      setSignInState(SignInState.ShowResetPassword);
    }
  };

  const onResetPasswordClick = async () => {
    setSignInState(SignInState.ShowResetPassword);
    setMessage("");
  };

  const onBackClick = () => {
    setSignInState(SignInState.ShowSignIn);
    setMessage("");
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 11, pb: 4 }}>
      <Paper elevation={3} sx={{ pt: 9, pb: 3, pl: 4, pr: 4, position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 125,
            height: 125,
            bgcolor: 'white',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: 3,
          }}
        >
          <img src={DiactiveLogo} alt="Diactive Logo" style={{ width: '80%', height: '80%' }} />
        </Box>
        <Stack spacing={2} alignItems="center">
          {(signInState === SignInState.ShowSignIn || signInState === SignInState.SubmitSignIn) && (
            <Typography variant="h5" component="h1" gutterBottom align="center">
              Iniciar sesión
            </Typography>
          )}
          {(signInState === SignInState.ShowResetPassword || signInState === SignInState.SubmitResetPassword) && (
            <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch', width: '100%', mb: 0.5 }}>
              <IconButton
                onClick={onBackClick}
                disabled={signInState === SignInState.SubmitResetPassword}
                aria-label="Volver al inicio de sesión"
                edge="start"
                size="large"
              >
                <ArrowBack color="primary" sx={{ fontSize: 40 }} />
              </IconButton>
              <Typography variant="h5" component="h1" sx={{ ml: 0.5 }}>
                Recuperar contraseña
              </Typography>
            </Box>
          )}
          <Box sx={{ width: '100%' }}>
            {message && <Alert severity={messageType}>{message}</Alert>}
            <form onSubmit={onSubmit}>
              <TextField
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={onEmailChange}
                fullWidth
                margin="normal"
              />
              {
                (signInState <= SignInState.SubmitSignIn) &&
                <TextField
                  label="Contraseña"
                  type="password"
                  value={password}
                  onChange={onPasswordChange}
                  fullWidth
                  margin="normal"
                />
              }
              {
                signInState === SignInState.ShowSignIn &&
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={onResetPasswordClick}
                  sx={{ mt: 2 }}
                >
                  ¿Has olvidado tu contraseña?
                </Button>
              }
              {
                signInState === SignInState.ShowSignIn &&
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Iniciar sesión
                </Button>
              }
              {
                signInState === SignInState.ShowResetPassword &&
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 1.5 }}
                >
                  Enviar
                </Button>
              }
              {
                (signInState === SignInState.SubmitSignIn || signInState === SignInState.SubmitResetPassword) &&
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5, mb: 3 }}>
                  <CircularProgress size={45} />
                </Box>
              }
            </form>
          </Box>
          {(signInState === SignInState.ShowSignIn || signInState === SignInState.ShowResetPassword) && (
            <ContactUsButton sx={{ mt: 1 }} />
          )}
        </Stack>
      </Paper>
      <Paper elevation={3} sx={{ mt: 3, mx: { sm: -3 }, p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" align="center" sx={{ fontWeight: 500, mb: 2 }}>
          Financiado por
        </Typography>
        <Stack spacing={{ xs: 1.5, sm: 2 }}>
          {sponsorLogoRows.map((row) => (
            <Box
              key={row.map((logo) => logo.alt).join()}
              sx={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', gap: { xs: 1.5, sm: 3 } }}
            >
              {row.map((logo) => (
                <Box
                  key={logo.alt}
                  component="img"
                  src={logo.src}
                  alt={logo.alt}
                  sx={{ minWidth: 0, maxWidth: `${100 / row.length}%`, maxHeight: logo.maxHeight, objectFit: 'contain' }}
                />
              ))}
            </Box>
          ))}
        </Stack>
        <Typography
          variant="body2"
          align="center"
          sx={{ mt: 2.5, pt: 2, borderTop: `2px solid ${fundingNavy}`, color: 'text.secondary', '& strong': { color: fundingNavy } }}
        >
          Este proyecto ha recibido financiación del <strong>Instituto de Salud Carlos III (ISCIII)</strong>,
          cofinanciada por la <strong>Unión Europea</strong> a través del <strong>Fondo Europeo de Desarrollo
          Regional (FEDER)</strong> (números de expediente: PI21/01238 y PI24/00829).
        </Typography>
      </Paper>
    </Container>
  );
};

export default SignIn;